/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { jsx } from '@compiled/react';

import { LinkButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { ConfluenceLogo } from '@atlaskit/logo';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Stack, Text, Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { ExperienceContext, ExperienceTrackerContext } from '@atlassian/experience-tracker';

import { ExperienceReasons } from '../experience-tracker/ExperienceNames';
import { type useUrl } from './useUrl';
import LoginToConfluenceIconUrl from '../assets/LoginPageIconSVG.svg';
import messages from './messages';
import { usePerformanceMetrics } from '../performance-metrics/performanceMetricsProvider';

const LOGIN_ISSUES_RELATED_TO_BROWSER_COOKIES =
	'https://support.atlassian.com/atlassian-account/docs/login-issues-related-to-browser-cookies/';

const styles = cssMap({
	loginButtonWrapper: { marginTop: token('space.400'), marginBottom: token('space.200') },
	mainWrapper: {
		display: 'inline-grid',
		alignItems: 'center',
		justifyItems: 'center',
		textAlign: 'center',
	},
});

export type ErrorScreenProps = {
	standaloneConfluenceUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
};

export const ErrorScreen = ({ standaloneConfluenceUrl }: ErrorScreenProps): JSX.Element => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const experience = useContext(ExperienceContext);

	const { formatMessage } = useIntl();

	const { cancelIframeLoadMetrics } = usePerformanceMetrics();

	useEffect(() => {
		cancelIframeLoadMetrics();
	}, [cancelIframeLoadMetrics]);

	useEffect(() => {
		experienceTracker.abort({
			name: experience,
			reason: ExperienceReasons.UNABLE_TO_DISPLAY_CONTENT,
		});
	}, [experience, experienceTracker]);

	return (
		<div css={styles.mainWrapper} data-testid="error-screen-container">
			<Stack space="space.250" alignInline="center">
				<ConfluenceLogo size="large" appearance="brand" />
				<img src={LoginToConfluenceIconUrl} alt={formatMessage(messages.loginToConfluenceIconAriaLabel)} width="148" height="160" />
				<Heading as="h2" size="large">
					{formatMessage(messages.paragraphLogin)}
				</Heading>
			</Stack>
			<Box
				xcss={styles.loginButtonWrapper}
				// data tag for JSM KB to target for excluding LinkButton from global styles
				// used instead of className as the className prop is not allowed
				// suggested by #help-design-system https://atlassian.slack.com/archives/CFJ9DU39U/p1746963980938489
				data-embedded-confluence-no-global-styles-link-button="true"
			>
				<LinkButton
					appearance="primary"
					aria-label={formatMessage(messages.buttonLoginToConfluenceNewTab)}
					iconAfter={LinkExternalIcon}
					href={standaloneConfluenceUrl ?? ''}
					target="_blank"
				>
					{formatMessage(messages.buttonLoginToConfluenceNewTab)}
				</LinkButton>
			</Box>
			<Stack space="space.150">
				<Text as="p">{formatMessage(messages.paragraphAlreadyLoggedIn)}</Text>
				<Text as="p">
					{formatMessage(messages.paragraphStillHavingTrouble, {
						link: (
							<Link
								href={LOGIN_ISSUES_RELATED_TO_BROWSER_COOKIES}
								target="_blank"
								aria-label={formatMessage(messages.linkAriaLabel)}
							>
								{formatMessage(messages.linkTroubleshootingText)}
							</Link>
						),
					})}
				</Text>
			</Stack>
		</div>
	);
};
